import { useState } from 'react';
import { CheckIcon, CopyIcon } from 'lucide-react';
import { cn } from '../lib/utils';

interface CopyableIdProps {
  /** The id to display + copy (e.g. `agt_V1StGXR8Z5jdHi6BmyT8aQ`). */
  id: string;
  className?: string;
  /** Optional aria label override. Defaults to "Copy id". */
  label?: string;
}

/**
 * Inline monospace pill that renders an entity id and copies it to the
 * clipboard on click. Surfaces the new prefixed-ID format on entity
 * detail panels (agents, MCP configs, personas, etc.).
 */
export function CopyableId({ id, className, label = 'Copy id' }: CopyableIdProps) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      // Clipboard access denied (e.g. in a sandboxed iframe). Silently no-op.
    }
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={label}
      title={copied ? 'Copied!' : label}
      className={cn(
        'group inline-flex items-center gap-1 rounded-md border border-input bg-muted/40 px-2 py-0.5 font-mono text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors',
        className,
      )}
    >
      <span className="select-all">{id}</span>
      {copied ? (
        <CheckIcon className="size-3 text-emerald-500" aria-hidden="true" />
      ) : (
        <CopyIcon className="size-3 opacity-50 group-hover:opacity-100" aria-hidden="true" />
      )}
    </button>
  );
}
